/**
 * Subscription campaign constants.
 *
 * Campaign slugs are validated server-side against Stripe:Campaigns config.
 * Clients never send raw Stripe coupon IDs.
 */

/** Annual intro-offer campaign slug (matches Stripe:Campaigns key). Ad default. */
export const INTRO_ANNUAL_CAMPAIGN_SLUG = 'intro-annual';

/**
 * Evergreen nurture-drip annual offer (40% first year when Stripe coupon is percent-off).
 * Used in nurture email CTAs: `/autopilot?campaign=nurture-annual-40`.
 */
export const NURTURE_ANNUAL_40_CAMPAIGN_SLUG = 'nurture-annual-40';

/** Query string key for campaign landing pages (`?campaign=`). */
export const CAMPAIGN_QUERY_PARAM = 'campaign';

/** sessionStorage key for persisting the campaign slug through MSAL signup. */
export const CAMPAIGN_SLUG_STORAGE_KEY = 'scheduledsmiles_campaign_slug';

/** Public route for the paid-only intro offer landing page (A/B control arm). */
export const INTRO_OFFER_ROUTE = '/autopilot';

/**
 * Public route for the redesigned intro-offer challenger page (A/B variant).
 * Same campaign resolution as /autopilot; GA4 distinguishes the arms by page path.
 */
export const NEVER_MISS_ROUTE = '/never-miss';

/**
 * Resolves the campaign slug from landing-page search params.
 * Missing or blank `campaign` defaults to intro-annual (paid ads / bare /autopilot).
 */
export function resolveCampaignSlugFromSearch(
  searchParams: URLSearchParams | { get: (key: string) => string | null },
): string {
  const raw = searchParams.get(CAMPAIGN_QUERY_PARAM)?.trim() ?? '';
  return raw.length > 0 ? raw : INTRO_ANNUAL_CAMPAIGN_SLUG;
}

/**
 * Builds the post-signup returnTo path for a campaign landing page.
 * Intro-annual omits the query so ad traffic stays on a clean URL; other slugs
 * keep `?campaign=` so auto-resume loads the same offer.
 */
export function buildCampaignLandingReturnTo(baseRoute: string, campaignSlug: string): string {
  if (campaignSlug === INTRO_ANNUAL_CAMPAIGN_SLUG) {
    return baseRoute;
  }

  return `${baseRoute}?${CAMPAIGN_QUERY_PARAM}=${encodeURIComponent(campaignSlug)}`;
}
